/** @file quadHandleAt: the quad handle under a screen point (nearest handle, else along an edge). */
import { QUAD_HANDLE_SPOTS } from './quadHandleSpots';
import { segmentDistance } from './segmentDistance';
import type { QuadHandle } from './viewportCtx';

type P2 = readonly [number, number];

/** The handle point nearest to `p` within `radius`, the pivot (checked first) winning a tie; or null. */
function nearest(points: readonly P2[], p: P2, radius: number): QuadHandle | null {
  let best: QuadHandle | null = null;
  let bestD = radius * radius;
  for (const h of [8, 0, 1, 2, 3, 4, 5, 6, 7] as const) {
    const q = points[h];
    if (q === undefined) continue;
    const d = (q[0] - p[0]) ** 2 + (q[1] - p[1]) ** 2;
    if (d < bestD || (best === null && d === bestD)) { bestD = d; best = h; }
  }
  return best;
}

/** The edge (4 top, 5 right, 6 bottom, 7 left) whose whole length passes within `radius` of `p`, nearest first. */
function edgeAt(points: readonly P2[], p: P2, radius: number): QuadHandle | null {
  let best: QuadHandle | null = null;
  let bestD = radius;
  for (const h of [4, 5, 6, 7] as const) {
    const [u, v] = QUAD_HANDLE_SPOTS[h] ?? [0, 0];
    // the edge's two corners: the spots that share its fixed parameter
    const ends = ([0, 1, 2, 3] as const).filter((k) => (u === 0.5 ? QUAD_HANDLE_SPOTS[k]?.[1] === v : QUAD_HANDLE_SPOTS[k]?.[0] === u));
    const a = points[ends[0] ?? 0];
    const b = points[ends[1] ?? 0];
    if (a === undefined || b === undefined) continue;
    const d = segmentDistance(p[0], p[1], a, b);
    if (d <= bestD) { bestD = d; best = h; }
  }
  return best;
}

/**
 * `points` from `quadHandlePoints` (target px), `radius` the grab radius (target px). The nearest handle point within
 * the radius wins (the pivot diamond on a tie, so a bottom-centre pivot beats the bottom edge's midpoint); away from
 * every point, an edge anywhere along its length (so an edge whose midpoint hides under the pivot is still grabbed
 * beside it); null when none is near.
 */
export function quadHandleAt(points: readonly P2[], p: P2, radius: number): QuadHandle | null {
  return nearest(points, p, radius) ?? edgeAt(points, p, radius * 0.75);
}
